import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { createWeb3Modal } from '@web3modal/wagmi/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WagmiProvider, useAccount, useDisconnect, useBalance, useChainId, useSwitchChain } from 'wagmi';
import { wagmiConfig, projectId, blokistaChain } from '../config/walletconnect';
import { web3Service, BLOKISTA_NETWORK, CONTRACT_ADDRESSES } from '../services/web3';
import { ethers } from 'ethers';

// Create QueryClient
const queryClient = new QueryClient();

// Create Web3Modal
createWeb3Modal({
  wagmiConfig,
  projectId,
  themeMode: 'dark',
  themeVariables: {
    '--w3m-accent': '#eab308',
    '--w3m-color-mix': '#eab308',
    '--w3m-color-mix-strength': 20,
  },
  featuredWalletIds: [
    'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
    '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0', // Trust Wallet
  ],
  enableAnalytics: false,
});

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

// Inner provider that uses wagmi hooks
const WalletProviderInner = ({ children }) => {
  const { address, isConnected: wagmiConnected, connector } = useAccount();
  const { disconnect: wagmiDisconnect } = useDisconnect();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  
  const [balances, setBalances] = useState({});
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  
  const isCorrectNetwork = chainId === BLOKISTA_NETWORK.chainId;

  // Initialize provider and signer when connected
  useEffect(() => {
    const initProvider = async () => {
      if (!wagmiConnected || !address) {
        setProvider(null);
        setSigner(null);
        web3Service.provider = null;
        web3Service.signer = null;
        web3Service.address = null;
        web3Service.isConnected = false;
        return;
      }
      try {
        // Prefer the active wagmi connector's provider (works for WalletConnect,
        // Trust, MetaMask, Rabby, Coinbase, etc). Fall back to window.ethereum.
        let rawProvider = null;
        if (connector && typeof connector.getProvider === 'function') {
          try {
            rawProvider = await connector.getProvider();
          } catch (e) {
            console.warn('connector.getProvider() failed, falling back to window.ethereum', e);
          }
        }
        if (!rawProvider && typeof window !== 'undefined' && window.ethereum) {
          rawProvider = window.ethereum;
        }
        if (!rawProvider) {
          console.error('No EIP-1193 provider available from wallet');
          return;
        }

        const newProvider = new ethers.providers.Web3Provider(rawProvider, 'any');

        // Choosing the right signer is subtle and the source of MetaMask
        // 4100 "method/account not authorized" errors:
        //   • `getSigner()` with no arg → ethers omits `from`, MetaMask uses
        //     its active account. Works for MetaMask but on WalletConnect /
        //     Trust this defaulted to account #0, which may not be the
        //     user's selected account.
        //   • `getSigner(address)` with wagmi's cached address → forces that
        //     `from` into every tx. If MetaMask currently has a *different*
        //     account active (or didn't authorize that exact address), it
        //     rejects with 4100.
        // The robust fix: query the wallet's *currently authorized* accounts
        // via eth_accounts, and pin the signer to the matching one. This
        // works for both MetaMask and WalletConnect/Trust.
        let signerAddress = address;
        try {
          const accounts = await rawProvider.request({ method: 'eth_accounts' });
          if (Array.isArray(accounts) && accounts.length > 0) {
            const match = accounts.find(
              (a) => a && a.toLowerCase() === address.toLowerCase()
            );
            // Prefer the address wagmi reports if the wallet still has it
            // authorized; otherwise fall back to the wallet's primary
            // authorized account (accounts[0]).
            signerAddress = match || accounts[0];
            if (!match) {
              console.warn(
                `[WalletContext] wagmi-reported address ${address} is no longer authorized by the wallet. ` +
                  `Falling back to ${accounts[0]}. User may have switched accounts in their wallet.`
              );
            }
          }
        } catch (e) {
          console.warn('[WalletContext] eth_accounts probe failed:', e);
        }

        const newSigner = newProvider.getSigner(signerAddress);
        setProvider(newProvider);
        setSigner(newSigner);

        // Also update web3Service singleton used across the app
        web3Service.provider = newProvider;
        web3Service.signer = newSigner;
        web3Service.address = signerAddress;
        web3Service.chainId = chainId;
        web3Service.isConnected = true;
        web3Service.initContracts();
      } catch (err) {
        console.error('Error initializing provider:', err);
      }
    };
    initProvider();
  }, [wagmiConnected, address, chainId, connector]);

  // Load balances when connected.
  // `tokens` is an optional array of { id, address, isNative, decimals } to fetch balances for.
  // Always fetches the native BCC balance.
  // Uses a dedicated Blokista RPC provider so balances work even if the wallet is
  // currently pointed at a different network.
  const loadBalances = useCallback(async (walletAddress, tokens = []) => {
    if (!walletAddress) return;

    try {
      const { BLOKISTA_NETWORK: NET } = await import('../services/web3');
      const rpc = new ethers.providers.JsonRpcProvider(NET.rpcUrls[0]);

      const nativeRaw = await rpc.getBalance(walletAddress);
      const nativeFloat = parseFloat(ethers.utils.formatEther(nativeRaw)) || 0;

      const realBalances = {
        bcc: nativeFloat,
      };

      // Default known tokens (WBCC, BCUSD) so swap/header UI still works
      const defaultTokens = [
        { id: 'wbcc', address: CONTRACT_ADDRESSES.WBCC },
        { id: 'bcusd', address: CONTRACT_ADDRESSES.BCUSD },
      ];

      const allTokens = [...defaultTokens, ...tokens].filter(
        (t) => t && t.address && t.address !== ethers.constants.AddressZero
      );

      const erc20Abi = [
        'function balanceOf(address owner) view returns (uint256)',
        'function decimals() view returns (uint8)',
      ];

      await Promise.all(
        allTokens.map(async (token) => {
          if (token.isNative) {
            if (token.id) realBalances[token.id] = nativeFloat;
            return;
          }
          try {
            const contract = new ethers.Contract(token.address, erc20Abi, rpc);
            const raw = await contract.balanceOf(walletAddress);
            const dec = token.decimals || 18;
            const value = parseFloat(ethers.utils.formatUnits(raw, dec)) || 0;
            if (token.id) realBalances[token.id] = value;
            realBalances[token.address.toLowerCase()] = value;
          } catch (err) {
            console.error(`[WalletContext] Balance fetch failed for ${token.address}:`, err);
            if (token.id) realBalances[token.id] = 0;
            realBalances[token.address.toLowerCase()] = 0;
          }
        })
      );

      setBalances((prev) => ({ ...prev, ...realBalances }));
    } catch (err) {
      console.error('Error loading balances:', err);
      setBalances({ bcc: 0 });
    }
  }, []);

  // Load balances when address changes
  useEffect(() => {
    if (address && wagmiConnected) {
      loadBalances(address);
    }
  }, [address, wagmiConnected, loadBalances]);

  const connectWallet = useCallback(async () => {
    // Web3Modal handles connection via its button
    // This is kept for backward compatibility
    setIsConnecting(true);
    try {
      // Open Web3Modal
      const { open } = await import('@web3modal/wagmi/react');
      open();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsConnecting(false);
    }
  }, []);

  const disconnectWallet = useCallback(() => {
    wagmiDisconnect();
    web3Service.disconnect();
    setBalances({});
    setProvider(null);
    setSigner(null);
  }, [wagmiDisconnect]);

  const getBalance = useCallback(
    (tokenIdOrAddress) => {
      if (!tokenIdOrAddress) return 0;
      const key = tokenIdOrAddress.toString();
      if (balances[key] !== undefined) return balances[key];
      const lc = key.toLowerCase();
      return balances[lc] || 0;
    },
    [balances]
  );

  const switchNetwork = useCallback(async () => {
    try {
      if (switchChain) {
        await switchChain({ chainId: BLOKISTA_NETWORK.chainId });
      } else {
        await web3Service.switchNetwork();
      }
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [switchChain]);

  const value = {
    isConnected: wagmiConnected,
    address,
    balances,
    isConnecting,
    chainId,
    isCorrectNetwork,
    error,
    connectWallet,
    disconnectWallet,
    getBalance,
    switchNetwork,
    loadBalances,
    networkConfig: BLOKISTA_NETWORK,
    web3Service,
    provider,
    signer,
    connector,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

// Main provider with Wagmi and QueryClient
export const WalletProvider = ({ children }) => {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <WalletProviderInner>
          {children}
        </WalletProviderInner>
      </QueryClientProvider>
    </WagmiProvider>
  );
};
